import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSyncAlt, FaCalendarAlt, FaMapMarkerAlt, FaBuilding, FaSpinner, FaExclamationCircle, FaCheckCircle, FaFilePdf, FaClock } from 'react-icons/fa';
import { useTheme } from '../../context/SocketContext';
import FloridaChecklistForm from './FloridaChecklistForm';
import WyomingChecklistForm from './WyomingChecklistForm';
import NewJerseyChecklistForm from './NewJerseyChecklistForm';
import DelawareLLCChecklistForm from './DelawareLLCChecklistForm';
import DelawareCCorpChecklistForm from './DelawareCCorpChecklistForm';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

const AnnualReportService = () => {
  const { currentThemeColors } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [annualReportData, setAnnualReportData] = useState(null);
  const [annualReportService, setAnnualReportService] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [userData, setUserData] = useState(null);
  const [showChecklistForm, setShowChecklistForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchAnnualReportData();
  }, []);

  // Helper function to format date for display
  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  const fetchAnnualReportData = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }

    try {
      setIsRefreshing(true);
      setError('');

      const userResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/auth/user`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!userResponse.ok) throw new Error('Failed to fetch user data');
      const data = await userResponse.json();
      setUserData(data.user);

      // Set annual report service data from user data
      if (data.user?.annualReportService) {
        setAnnualReportService(data.user.annualReportService);
      }

      const annualReportResponse = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/annual-report/${data.user._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!annualReportResponse.ok) {
        if (annualReportResponse.status === 404) {
          setAnnualReportData(null);
          return;
        }
        throw new Error('Failed to fetch annual report data');
      }

      const arData = await annualReportResponse.json();
      setAnnualReportData(arData);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleStepClick = (step) => {
    if (step === 1) {
      const step1 = userData?.annualReportSteps?.find(s => s.step === 1);
      if (step1?.status === 'submitted') {
        alert('Checklist has already been submitted.');
        return;
      }
      setShowChecklistForm(true);
    }
  };

  const generatePDF = (formData) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header
    doc.setFillColor(79, 70, 229);
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Florida Checklist', 20, 25);
    doc.setFontSize(10);
    doc.text('Annual Report Services', 20, 32);

    // Business Details
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.text('(A) Basic Business Details', 20, 55);
    
    doc.autoTable({
      startY: 60,
      head: [['Field', 'Value']],
      body: [
        ['Business Name', formData.businessName],
        ['EIN Number', formData.einNumber],
        ['Principal Address', formData.principalAddress],
        ['Mailing Address', formData.mailingAddress],
        ['Registered Agent', formData.registeredAgent],
        ['Registered Agent Address', formData.registeredAgentAddress],
      ],
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229] },
    });

    // Director Details
    doc.setFontSize(16);
    doc.text('(B) Director Detail', 20, doc.lastAutoTable.finalY + 15);

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Director', 'Name', 'Address']],
      body: [
        ['Director 1', formData.director1Name, formData.director1Address],
        ['Director 2', formData.director2Name, formData.director2Address],
        ['Director 3', formData.director3Name, formData.director3Address],
      ],
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229] },
    });

    // Email and Certification
    doc.setFontSize(16);
    doc.text('Email and Certification', 20, doc.lastAutoTable.finalY + 15);

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Field', 'Value']],
      body: [
        ['Email Address', formData.emailAddress],
        ['Certify Name', formData.certifyName],
        ['Title', formData.certifyTitle],
      ],
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229] },
    });

    // Footer
    const date = new Date().toLocaleString();
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated on: ${date}`, 20, doc.internal.pageSize.getHeight() - 10);

    return doc.output('datauristring');
  };

  const generateWyomingPDF = (formData) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header
    doc.setFillColor(37, 99, 235); // Blue-600
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Wyoming Checklist', 20, 25);
    doc.setFontSize(10);
    doc.text('Annual Report Services', 20, 32);

    // Section A: Mailing Address
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.text('(A) Mailing Address', 20, 55);
    
    doc.autoTable({
      startY: 60,
      head: [['Field', 'Value']],
      body: [
        ['Country', formData.mailingCountry],
        ['Address Line 1', formData.mailingAddr1],
        ['Address Line 2', formData.mailingAddr2],
        ['City', formData.mailingCity],
        ['State', formData.mailingState],
        ['Postal Code', formData.mailingPostalCode],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    // Section B: Principal Office Address
    doc.setFontSize(14);
    doc.text('(B) Principal Office Address', 20, doc.lastAutoTable.finalY + 15);

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Field', 'Value']],
      body: [
        ['Country', formData.principalCountry],
        ['Address Line 1', formData.principalAddr1],
        ['Address Line 2', formData.principalAddr2],
        ['City', formData.principalCity],
        ['State', formData.principalState],
        ['Postal Code', formData.principalPostalCode],
        ['Phone', formData.principalPhone],
        ['Email', formData.principalEmail],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    // Section C: Asset Worksheet & Fees
    doc.addPage();
    doc.setTextColor(37, 99, 235);
    doc.setFontSize(20);
    doc.text('Wyoming Asset Worksheet', 20, 20);

    doc.autoTable({
      startY: 30,
      head: [['Line', 'Asset Description', 'Value']],
      body: [
        ['1', 'Cash', `$${formData.cash}`],
        ['2a', 'Trade Notes & Accounts Receivable', `$${formData.tradeNotes}`],
        ['2b', 'Subtract Allowance for Bad Debts', `-$${formData.subtractAllowance}`],
        ['3', 'Inventories', `$${formData.inventories}`],
        ['4', 'U.S. Government Obligations', `$${formData.usGovObligations}`],
        ['5', 'Tax Exempt Securities', `$${formData.taxExemptSecurities}`],
        ['6', 'Other Current Assets', `$${formData.otherCurrentAssets}`],
        ['7', 'Loans to Stockholders', `$${formData.loansToStockholders}`],
        ['8', 'Mortgage and Real Estate Loans', `$${formData.mortgageRealEstateLoans}`],
        ['9', 'Other Investments', `$${formData.otherInvestments}`],
        ['10', 'Assets (real and personal)', `$${formData.depreciableAssets}`],
        ['11', 'Depletable assets', `$${formData.depletableAssets}`],
        ['12', 'Land (Assessed Valuation)', `$${formData.land}`],
        ['13a', 'Intangible Assets', `$${formData.intangibleAssets}`],
        ['13b', 'Subtract Accumulated Amortization', `-$${formData.subtractAmortization}`],
        ['14', 'Other Assets', `$${formData.otherAssets}`],
        ['15', 'TOTAL ASSET VALUE', `$${formData.totalAssetValue}`],
        ['', 'FINAL FILING FEE', `$${formData.filingFee}`],
      ],
      theme: 'grid',
      headStyles: { fillColor: [37, 99, 235] },
      columnStyles: { 0: { cellWidth: 20 }, 2: { halign: 'right' } },
    });

    // Section D: Certification
    doc.setFontSize(14);
    doc.setTextColor(0, 0, 0);
    doc.text('(D) Certification', 20, doc.lastAutoTable.finalY + 15);

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Field', 'Value']],
      body: [
        ['Name', `${formData.certifyFirstName} ${formData.certifyMiddleName} ${formData.certifyLastName}`],
        ['Email', formData.certifyEmail],
      ],
      theme: 'striped',
      headStyles: { fillColor: [37, 99, 235] },
    });

    // Footer
    const date = new Date().toLocaleString();
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated on: ${date}`, 20, doc.internal.pageSize.getHeight() - 10);

    return doc.output('datauristring');
  };

  const generateNJ_PDF = (formData) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header
    doc.setFillColor(220, 38, 38); // Red-600
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('New Jersey Checklist', 20, 25);
    doc.setFontSize(10);
    doc.text('Annual Report Services', 20, 32);

    // Section A: Registered Agent
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.text('(A) Registered Agent and Office', 20, 55);
    
    doc.autoTable({
      startY: 60,
      head: [['Field', 'Value']],
      body: [
        ['Registered Agent', formData.raName],
        ['Registered Agent #', formData.raNumber],
        ['Address Line 1', formData.raAddr1],
        ['Address Line 2', formData.raAddr2],
        ['City, State, Zip', formData.raCityStateZip],
      ],
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 38] },
    });

    // Section B: Business Address Change
    doc.setFontSize(14);
    doc.text('(B) Business Address Change', 20, doc.lastAutoTable.finalY + 15);
    doc.setFontSize(10);
    doc.text(formData.businessAddressChange || 'No changes reported.', 20, doc.lastAutoTable.finalY + 22, { maxWidth: 170 });

    // Section C: Directors
    doc.setFontSize(14);
    doc.text('(C) List of Directors', 20, doc.lastAutoTable.finalY + 40);

    const directorRows = formData.directors.map(d => [d.name, d.title, d.address]);
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 45,
      head: [['Name', 'Title', 'Address']],
      body: directorRows,
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 38] },
    });

    // Section D: Workers Comp
    doc.setFontSize(14);
    doc.text('(D) Workers\' Compensation', 20, doc.lastAutoTable.finalY + 15);
    doc.setFontSize(10);
    doc.text(`Required to provide worker's compensation insurance: ${formData.workersCompRequired}`, 20, doc.lastAutoTable.finalY + 22);

    // Section E: Authorization
    doc.setFontSize(14);
    doc.text('(E) Authorization', 20, doc.lastAutoTable.finalY + 40);

    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 45,
      head: [['Field', 'Value']],
      body: [
        ['Authorizer Name', formData.authName],
        ['Title', formData.authTitle],
        ['Phone Number', formData.authPhone],
        ['Address', formData.authAddress],
      ],
      theme: 'striped',
      headStyles: { fillColor: [220, 38, 38] },
    });

    // Footer
    const date = new Date().toLocaleString();
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated on: ${date}`, 20, doc.internal.pageSize.getHeight() - 10);

    return doc.output('datauristring');
  };

  const generateDelawareLLCPDF = (formData) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(13, 148, 136); // Teal-600
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Delaware LLC Checklist', 20, 25);
    doc.setFontSize(10);
    doc.text('Annual Report Services', 20, 32);

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.text('Checklist Details', 20, 55);
    
    doc.autoTable({
      startY: 60,
      head: [['Field', 'Value']],
      body: [
        ['Company Name', formData.companyName],
        ['Tax Year', formData.taxYear],
        ['Agent Name', formData.raName],
        ['Agent Address', formData.raAddress],
        ['City', formData.raCity],
        ['State', formData.raState],
        ['Zip Code', formData.raZipCode],
      ],
      theme: 'striped',
      headStyles: { fillColor: [13, 148, 136] },
    });

    const date = new Date().toLocaleString();
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated on: ${date}`, 20, doc.internal.pageSize.getHeight() - 10);
    return doc.output('datauristring');
  };

  const generateDelawareCCorpPDF = (formData) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFillColor(79, 70, 229); // Indigo-600
    doc.rect(0, 0, pageWidth, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Delaware C-CORP Checklist', 20, 25);
    doc.setFontSize(10);
    doc.text('Annual Report Services', 20, 32);

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.text('1. Principal Place of Business', 20, 55);
    doc.autoTable({
      startY: 60,
      head: [['Field', 'Value']],
      body: [
        ['Fiscal Year End', formData.fiscalYearEnd],
        ['Street Address', formData.principalStreet],
        ['City', formData.principalCity],
        ['State', formData.principalState],
        ['Zip', formData.principalZip],
        ['Country', formData.principalCountry],
        ['Phone', formData.principalPhone],
        ['Email', formData.principalEmail],
        ['Nature of Business', formData.natureOfBusiness],
      ],
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229] },
    });

    doc.setFontSize(14);
    doc.text('2. Officer Information', 20, doc.lastAutoTable.finalY + 15);
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Field', 'Value']],
      body: [
        ['Name', `${formData.officerFirstName} ${formData.officerMiddleName} ${formData.officerLastName}`],
        ['Title', formData.officerTitle],
        ['Address', `${formData.officerStreet}, ${formData.officerCity}, ${formData.officerState} ${formData.officerZip}, ${formData.officerCountry}`],
      ],
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229] },
    });

    doc.addPage();
    doc.setFontSize(14);
    doc.text('3. Directors Information', 20, 20);
    doc.autoTable({
      startY: 25,
      head: [['Name', 'Address']],
      body: formData.directors.map(d => [
        `${d.firstName} ${d.middleName} ${d.lastName}`,
        `${d.street}, ${d.city}, ${d.state} ${d.zip}, ${d.country}`
      ]),
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229] },
    });

    doc.setFontSize(14);
    doc.text('4. Authorization Person', 20, doc.lastAutoTable.finalY + 15);
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Field', 'Value']],
      body: [
        ['Name', `${formData.authFirstName} ${formData.authMiddleName} ${formData.authLastName}`],
        ['Title', formData.authTitle],
        ['Address', `${formData.authStreet}, ${formData.authCity}, ${formData.authState} ${formData.authZip}, ${formData.authCountry}`],
      ],
      theme: 'striped',
      headStyles: { fillColor: [79, 70, 229] },
    });

    const date = new Date().toLocaleString();
    doc.setFontSize(10);
    doc.setTextColor(150, 150, 150);
    doc.text(`Generated on: ${date}`, 20, doc.internal.pageSize.getHeight() - 10);
    return doc.output('datauristring');
  };

  const handleSaveChecklist = async (formData, isFinal = true) => {
    setIsSubmitting(true);
    const token = localStorage.getItem('token');

    try {
      if (isFinal) {
        let pdfBase64;
        if (annualReportService?.state === 'Wyoming') {
          pdfBase64 = generateWyomingPDF(formData);
        } else if (annualReportService?.state === 'New Jersey') {
          pdfBase64 = generateNJ_PDF(formData);
        } else if (annualReportService?.state === 'Delaware') {
          pdfBase64 = annualReportService?.entityType === 'LLC' 
            ? generateDelawareLLCPDF(formData)
            : generateDelawareCCorpPDF(formData);
        } else {
          pdfBase64 = generatePDF(formData);
        }

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/annual-report/submit-checklist/${userData._id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ formData, pdfBase64 })
        });

        if (!response.ok) throw new Error('Failed to submit checklist');

        alert('Checklist submitted successfully! A copy has been saved to your Documents.');
        setShowChecklistForm(false);
      } else {
        // Save as Draft
        const updatedSteps = (userData.annualReportSteps || [
          { step: 1, title: 'Initial Information Required', status: 'pending' },
          { step: 2, title: 'Annual Report Filed', status: 'pending' }
        ]).map(s => {
          if (s.step === 1) {
            return { ...s, status: 'in-progress', answers: formData };
          }
          return s;
        });

        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/annual-report/steps/${userData._id}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ steps: updatedSteps })
        });

        if (!response.ok) throw new Error('Failed to save draft');

        alert('Draft saved successfully!');
      }
      
      fetchAnnualReportData();
    } catch (err) {
      alert('Error: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStepStatus = (step) => {
    if (!userData?.annualReportSteps) return step === 1 ? 'in-progress' : 'pending';
    const s = userData.annualReportSteps.find(s => s.step === step);
    return s?.status || (step === 1 ? 'in-progress' : 'pending');
  };

  const getStepProgress = (step) => {
    if (step !== 1 || !userData?.annualReportSteps) return { progress: 0, total: 15 };
    const s = userData.annualReportSteps.find(s => s.step === 1);
    if (!s || !s.answers) return { progress: 0, total: 15 };
    
    let fields = [];
    if (annualReportService?.state === 'Wyoming') {
      fields = [
        'mailingCountry', 'mailingAddr1', 'mailingCity', 'mailingState', 'mailingPostalCode',
        'principalCountry', 'principalAddr1', 'principalCity', 'principalState', 'principalPostalCode',
        'principalPhone', 'principalEmail', 'certifyFirstName', 'certifyLastName', 'certifyEmail'
      ];
    } else if (annualReportService?.state === 'New Jersey') {
      fields = [
        'raName', 'raNumber', 'raAddr1', 'raCityStateZip', 
        'workersCompRequired', 'authName', 'authTitle', 'authPhone', 'authAddress'
      ];
    } else if (annualReportService?.state === 'Delaware') {
      if (annualReportService?.entityType === 'LLC') {
        fields = ['companyName', 'taxYear', 'raName', 'raAddress', 'raCity', 'raZipCode'];
      } else {
        fields = ['fiscalYearEnd', 'principalStreet', 'principalCity', 'natureOfBusiness', 'officerFirstName', 'officerLastName', 'authFirstName', 'authLastName'];
      }
    } else {
      fields = [
        'businessName', 'einNumber', 'principalAddress', 'mailingAddress', 
        'registeredAgent', 'registeredAgentAddress', 'director1Name', 'director1Address',
        'director2Name', 'director2Address', 'director3Name', 'director3Address',
        'emailAddress', 'certifyName', 'certifyTitle'
      ];
    }
    
    const answeredCount = fields.filter(f => s.answers[f] && s.answers[f].toString().trim() !== '').length;
    return { progress: answeredCount, total: fields.length };
  };

  const StepCard = ({ step, title, status, onClick, progressData }) => {
    const isCompleted = status === 'submitted' || status === 'completed';
    const isInProgress = status === 'in-progress';
    const isPending = status === 'pending';

    const getCardStyles = () => {
      switch (status) {
        case 'in-progress':
          return 'bg-[#1e2a3e] border-[#2d3f5a]';
        case 'client-pending':
          return 'bg-orange-600 border-orange-500';
        case 'submitted':
        case 'completed':
          return 'bg-green-600 border-green-500';
        default:
          return 'bg-blue-600 border-blue-500';
      }
    };

    const getBadgeStyles = () => {
      switch (status) {
        case 'in-progress':
          return 'bg-blue-600 text-white';
        case 'client-pending':
          return 'bg-orange-800 text-white';
        case 'submitted':
        case 'completed':
          return 'bg-green-800 text-white';
        default:
          return 'bg-blue-800 text-white';
      }
    };

    const getStatusBadge = () => {
      switch (status) {
        case 'in-progress':
          return 'IN PROGRESS';
        case 'client-pending':
          return 'ACTION REQUIRED';
        case 'submitted':
        case 'completed':
          return 'COMPLETED';
        default:
          return 'PENDING';
      }
    };

    const getStepInstructions = () => {
      if (step === 1) {
        if (annualReportService?.state === 'Wyoming') return "Fill the mailing, office, and asset details for Wyoming Annual Report.";
        if (annualReportService?.state === 'New Jersey') return "Fill the agent, office, and director details for New Jersey Annual Report.";
        if (annualReportService?.state === 'Delaware') {
          return annualReportService?.entityType === 'LLC' 
            ? "Fill the agent and tax year details for Delaware LLC."
            : "Fill the officer, director, and business details for Delaware C-CORP.";
        }
        return `Fill the required business details for ${annualReportService?.state || 'Florida'} Annual Report.`;
      }
      return "Administrative review and processing of your annual report.";
    };

    return (
      <div 
        onClick={() => onClick(step)}
        className={`group relative w-full p-5 rounded-2xl border cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 flex flex-col h-full ${getCardStyles()}`}
      >
        <div className="flex items-center justify-between gap-2 mb-3">
          <span className="text-white font-bold text-lg text-shadow">Step {step}</span>
          <span className={`px-3 py-1 rounded text-[10px] font-bold uppercase tracking-wide ${getBadgeStyles()}`}>
            {getStatusBadge()}
          </span>
        </div>

        <h3 className="text-white font-semibold text-base leading-snug flex-grow">
          {title}
        </h3>

        {/* Progress Bar for Step 1 */}
        {step === 1 && (status === 'in-progress' || status === 'submitted') && progressData && (
          <div className="mt-4 pt-2">
            <div className="flex items-center justify-between text-[11px] text-gray-300 mb-1">
              <span className="tracking-wide">Progress</span>
              <span className="font-semibold">{progressData.progress}/{progressData.total} • {Math.round((progressData.progress / progressData.total) * 100)}%</span>
            </div>
            <div className="h-1.5 bg-gray-600/50 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 bg-gray-300"
                style={{ width: `${(progressData.progress / progressData.total) * 100}%` }}
              />
            </div>
          </div>
        )}

        {isInProgress && (
          <div className="mt-auto pt-3 flex items-center gap-2 text-xs text-blue-100 font-bold">
            <FaClock className="animate-pulse" /> Action Required
          </div>
        )}

        {isCompleted && (
          <div className="mt-auto pt-3 flex items-center gap-2 text-xs text-green-100 font-bold">
            <FaCheckCircle /> Completed
          </div>
        )}

        {isPending && (
          <div className="mt-auto pt-3 flex items-center gap-2 text-xs text-blue-100 font-medium">
            <FaSpinner className="w-3 h-3 animate-spin" />
            <span>Working for Employee</span>
          </div>
        )}

        {/* Tooltip on Hover */}
        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-4 py-3 bg-dark-800 dark:bg-dark-700 text-white text-xs rounded-2xl shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-normal text-center max-w-[250px] z-10">
          <span className="block">{getStepInstructions()}</span>
          <div className="absolute top-full left-1/2 -translate-x-1/2 border-8 border-transparent border-t-dark-800 dark:border-t-dark-700" />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <FaSpinner className="w-12 h-12 text-primary-500 animate-spin mx-auto" />
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading your annual report data...</p>
        </div>
      </div>
    );
  }

  if (showChecklistForm) {
    return (
      <div className="animate-fade-in">
        {annualReportService?.state === 'Wyoming' ? (
          <WyomingChecklistForm
            initialData={userData?.annualReportSteps?.find(s => s.step === 1)?.answers}
            onSave={handleSaveChecklist}
            onCancel={() => setShowChecklistForm(false)}
            isSubmitting={isSubmitting}
          />
        ) : annualReportService?.state === 'New Jersey' ? (
          <NewJerseyChecklistForm
            initialData={userData?.annualReportSteps?.find(s => s.step === 1)?.answers}
            onSave={handleSaveChecklist}
            onCancel={() => setShowChecklistForm(false)}
            isSubmitting={isSubmitting}
          />
        ) : annualReportService?.state === 'Delaware' ? (
          annualReportService?.entityType === 'LLC' ? (
            <DelawareLLCChecklistForm
              initialData={userData?.annualReportSteps?.find(s => s.step === 1)?.answers}
              onSave={handleSaveChecklist}
              onCancel={() => setShowChecklistForm(false)}
              isSubmitting={isSubmitting}
            />
          ) : (
            <DelawareCCorpChecklistForm
              initialData={userData?.annualReportSteps?.find(s => s.step === 1)?.answers}
              onSave={handleSaveChecklist}
              onCancel={() => setShowChecklistForm(false)}
              isSubmitting={isSubmitting}
            />
          )
        ) : (
          <FloridaChecklistForm 
            initialData={userData?.annualReportSteps?.find(s => s.step === 1)?.answers}
            onSave={handleSaveChecklist}
            onCancel={() => setShowChecklistForm(false)}
            isSubmitting={isSubmitting}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Page Header */}

      {/* Error Alert */}
      {error && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
            <FaExclamationCircle className="w-5 h-5" />
            <span><strong>Error:</strong> {error}</span>
          </div>
          <button
            onClick={fetchAnnualReportData}
            disabled={isRefreshing}
            className="px-4 py-2 rounded-lg border border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
          >
            {isRefreshing ? 'Refreshing...' : 'Try Again'}
          </button>
        </div>
      )}

      {/* Annual Report Due Date Card */}
      {annualReportService?.dueDate && (
        <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '50ms', animationFillMode: 'forwards' }}>
          <div
            className="py-2.5 px-4 text-white flex items-center gap-3"
            style={{ background: `linear-gradient(135deg, #4f46e5, #9333ea)` }}
          >
            <div className="p-1.5 bg-white/20 rounded-lg backdrop-blur-sm">
              <FaCalendarAlt className="w-3.5 h-3.5" />
            </div>
            <div>
              <h2 className="text-sm font-bold tracking-tight leading-none">Annual Report Due Date</h2>
            </div>
          </div>

          <div className="p-2.5 md:p-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2.5 md:gap-3">
              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700/50 border border-gray-100 dark:border-dark-600 transition-all hover:shadow-md group">
                <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center text-primary-500 group-hover:scale-110 transition-transform flex-shrink-0">
                  <FaMapMarkerAlt className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] uppercase font-bold text-gray-400 dark:text-gray-500 tracking-widest mb-0.5 truncate">State</p>
                  <p className="text-sm font-bold text-dark-800 dark:text-white truncate">{annualReportService.state}</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700/50 border border-gray-100 dark:border-dark-600 transition-all hover:shadow-md group">
                <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 group-hover:scale-110 transition-transform flex-shrink-0">
                  <FaBuilding className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] uppercase font-bold text-gray-400 dark:text-gray-500 tracking-widest mb-0.5 truncate">Entity Type</p>
                  <p className="text-sm font-bold text-dark-800 dark:text-white truncate">{annualReportService.entityType}</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700/50 border border-gray-100 dark:border-dark-600 transition-all hover:shadow-md group">
                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-500 group-hover:scale-110 transition-transform flex-shrink-0">
                  <FaCalendarAlt className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] uppercase font-bold text-gray-400 dark:text-gray-500 tracking-widest mb-0.5 truncate">Formation Date</p>
                  <p className="text-sm font-bold text-dark-800 dark:text-white truncate">{formatDateForDisplay(annualReportService.registrationDate)}</p>
                </div>
              </div>

              <div className="flex items-center gap-2.5 p-2.5 rounded-xl bg-gray-50 dark:bg-dark-700/50 border border-gray-100 dark:border-dark-600 transition-all hover:shadow-md group">
                <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-500 group-hover:scale-110 transition-transform flex-shrink-0">
                  <FaCalendarAlt className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <p className="text-[9px] uppercase font-bold text-gray-400 dark:text-gray-500 tracking-widest mb-0.5 truncate">Filing Year</p>
                  <p className="text-sm font-bold text-dark-800 dark:text-white truncate">{annualReportService.filingYear || 'N/A'}</p>
                </div>
              </div>

              {(() => {
                const text = annualReportService.dueDateText || formatDateForDisplay(annualReportService.dueDate);
                const deadlineMatch = text.match(/(.*)\s*\((Final deadline:.*)\)/);

                if (deadlineMatch) {
                  const finalDeadline = deadlineMatch[2].replace('Final deadline:', '').replace(')', '').trim();

                  return (
                    <div className="h-full p-2.5 rounded-xl bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/10 dark:to-orange-900/10 border-2 border-red-500/10 flex flex-col justify-center items-center text-center relative overflow-hidden">
                      <div>
                        <p className="text-[8px] uppercase font-black text-red-600 tracking-[0.2em] mb-0.5 animate-pulse">Due Date</p>
                        <p className="text-sm font-black text-red-600 dark:text-red-500 leading-none">
                          {finalDeadline}
                        </p>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="h-full p-2.5 rounded-xl bg-red-600 text-white text-center shadow-lg shadow-red-600/20 flex flex-col justify-center">
                    <p className="text-[8px] uppercase font-black tracking-widest opacity-70 mb-0.5">Due Date</p>
                    <p className="text-base font-black">{text}</p>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Annual Report Steps Section */}
      {(annualReportService?.state === 'Florida' || annualReportService?.state === 'Wyoming' || annualReportService?.state === 'New Jersey' || annualReportService?.state === 'Delaware') && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
          <StepCard 
            step={1} 
            title="Initial Information Required" 
            status={getStepStatus(1)} 
            progressData={getStepProgress(1)}
            onClick={handleStepClick}
          />
          <StepCard 
            step={2} 
            title="Annual Report Filed" 
            status={getStepStatus(2)} 
            onClick={() => {}}
          />
        </div>
      )}

      {annualReportData && (
        <div className="space-y-6">
          {/* Data Table */}
          <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
            <div
              className="py-3.5 px-5 text-white flex items-center justify-between"
              style={{ background: `linear-gradient(135deg, #4f46e5, #9333ea)` }}
            >
              <div className="flex items-center gap-3">
                <h2 className="text-sm font-bold tracking-tight">{annualReportData.additionalTitle || 'Annual Report Records'}</h2>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-dark-700">
                  <tr>
                    {annualReportData.headers.map((header, index) => (
                      <th key={index} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                  {annualReportData.rows.filter(row => !row.include).map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white dark:bg-dark-800' : 'bg-gray-50 dark:bg-dark-700/50'}>
                      {annualReportData.headers.map((header, colIndex) => (
                        <td key={colIndex} className="px-4 py-3 text-sm text-dark-800 dark:text-gray-300">
                          {typeof row[header] === 'number' ? `$${row[header].toLocaleString()}` : row[header]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* Placeholder when no setup exists */}
      {!annualReportService?.dueDate && !annualReportData && !loading && (
        <div className="card overflow-hidden opacity-0 animate-fade-in-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
          <div
            className="py-12 px-6 text-center flex flex-col items-center justify-center space-y-4"
            style={{ background: `linear-gradient(135deg, rgba(79, 70, 229, 0.05), rgba(147, 51, 234, 0.05))` }}
          >
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-lg shadow-indigo-500/20 mb-2">
              <FaCalendarAlt className="w-8 h-8" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Annual Report Setup Pending</h2>
            <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto">
              Our team is currently setting up your annual report tracking. Once the administrative setup is complete, your due dates and filing records will appear here.
            </p>
            <div className="pt-4">
              <button 
                onClick={fetchAnnualReportData}
                className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-indigo-500/30 transition-all flex items-center gap-2"
              >
                <FaSyncAlt className={isRefreshing ? "animate-spin" : ""} />
                Check for Updates
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnnualReportService;

